/*
 * Copyright (c) 2008-2019 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

var NetworkStatus, getConfigParameter, logping, log, OutBuffer, triggerAuthentication
var apiMeCounter = 0 // Counter to call /api/me (to verify the token is still valid) only once in onError

if (typeof require === "function") {
  var storage = require("../utils/Storage")
  getStorageObject = storage.getStorageObject
  setStorageObject = storage.setStorageObject

  var logging = require("../utils/log")
  log = logging.log
  logping = logging.logping

  NetworkStatus = require("./NetworkStatus")
  OutBuffer = require("./OutBuffer")
  var c = require("../config")
  getConfigParameter = c.getConfigParameter

  var auth = require("@/auth/OAuth2AuthorizationCode")
  triggerAuthentication = auth.triggerAuthentication
}

/**
 * The connection bundling the plain websocket handling
 * @type {{connect, sendMessage, close}}
 */
function WebsocketConnection(serverUrl, connectionId, persistentContributionQueue, communication) {
  var self = this

  /** The underlying websocket */
  var websocket = null

  /** the websocket URL to connect to; if undefined or null reconnect() will fail */
  var url

  /** current status of the network communication: 0 - OK, 1 - Warning, 2 - Offline */
  var networkStatus = NetworkStatus.DISCONNECTED

  // message handling
  var outBuffer = new OutBuffer(communication)
  var lastReceivedId = -1
  var serverLastAckedId = -1
  var ackRequired = false
  var lastMessageReceivedTimestamp = -1
  var connecting = false
  var closing = false
  var offlineTimeout = getConfigParameter("networkOfflineTimeout")
  var compressionEnabled = getConfigParameter("compressPayload")

  /** setInterval task to write messages */
  var writeTimer

  /** setInterval task to retry initial connect in case the connect fails */
  var initialAckTimeoutTimer

  // custom close event codes (staring from 4000)
  var CloseEventCode = {
    CLOSE_SHUTDOWN: 4000,
    CLOSE_RECONNECT: 4001,
    CLOSE_TIMEOUT: 4002,
  }

  var accessToken

  // --------------------------------- Websocket Connect ---------------------------------

  /**
   * connect to the websocket URL specified
   * @param accessToken the optional accessToken if authenticated via OAuth
   */
  function connect(token) {

    if (token) {
      accessToken = token
    }

    if (connecting) {
      log.info("CONNECT: Skipping connect - already connecting")
      return
    }
    connecting = true
    if (!serverUrl) {
      notifyProgrammingError(
        "WebsocketConnection.connect called without url passed and without previous url specified."
      )
      return
    }
    var path = "/ws/"
    if (compressionEnabled) {
      path = "/wsd/" // this uses deflate to compress each message (lower bandwidth, more computation required -> more battery consumption?)
    }
    path += connectionId

    if (accessToken) {
      path += "?access_token=" + accessToken
    }

    // append ip address if available
    if (typeof ipAddress !== "undefined" && ipAddress) {
      if (path.indexOf("?") >= 0) path += "&ipAddr=" + ipAddress
      else path += "?ipAddr=" + ipAddress
    }

    var idx = baseURL.indexOf("://")
    // secureCommunication can be used in a wrapper where the client is running locally
    url =
      (baseURL.startsWith("https:") || getConfigParameter("secureCommunication") ? "wss://" : "ws://") +
      baseURL.substring(idx + 3, baseURL.length) +
      path
    log.info("Connecting to websocket URL: ", url)

    if (websocket) {
      log.error("Trying to connect although there exists a websocket with readyState ", websocket.readyState)
      return
    }
    log.debug("CONNECT: Creating new websocket / old websocket: ", websocket)
    try {
      // the check if the WebSocket API is available in this browser must be made BEFORE calling connect!
      websocket = new WebSocket(url) // May result in 404 if server is currently starting, triggers onError
      websocket.onopen = onOpen
      websocket.onmessage = onMessage
      websocket.onclose = onClose
      websocket.onerror = onError
    } catch (e) {
      log.error("Failed to create websocket: ", e)
      close(CloseEventCode.CLOSE_RECONNECT, "Failed to create websocket - creating new one")
      delayedConnect()
    }
  }

  /**
   * onOpen is called once a new websocket has been created
   * it will set the initialAckTimeoutTimer
   *   a) as flag that we wait for the first message from the server
   *   b) as the timeout for the connect to the server
   * @param message
   */
  function onOpen(message) {
    log.debug("CONNECT: Websocket opened - waiting for initial ack from server")
    startInitialAckTimeoutTimer()
  }

  function startInitialAckTimeoutTimer() {
    stopInitialAckTimeoutTimer()
    var timeout = getConfigParameter("websocketInitialConnectTimeout")
    initialAckTimeoutTimer = setTimeout(function() {
      log.error("CONNECT: Initial ACK did not arrive in time. Reinitializing connection.")
      close(CloseEventCode.CLOSE_RECONNECT, "Initial ack for WebsocketConnection not received in time")
      var result = { reconnect: true, connection: self }
      app.manager.trigger("communication:initialConnectFailed", result)
      if (result.reconnect) {
        connect() // connect to the same url again
      }
    }, timeout)
    log.trace("startInitialAckTimeoutTimer: ", initialAckTimeoutTimer)
  }

  function stopInitialAckTimeoutTimer() {
    if (initialAckTimeoutTimer) {
      log.trace("stopInitialAckTimeoutTimer: ", initialAckTimeoutTimer)
      clearTimeout(initialAckTimeoutTimer)
      initialAckTimeoutTimer = null
    }
  }

  // --------------------------------- Incoming messages ---------------------------------

  /**
   * Called by underlying websocket implementation when messages are received
   *
   * if this is the first message received (i.e. initialAckTimeoutTimer is set)
   * the message write timer is started here
   *
   * @param message
   */
  function onMessage(message) {
    try {
      // on the client side the websocket is notified after a couple of minutes when no pings from the server are received
      // (at least in chrome, might be browser dependent)
      // so we track a high-level ping ourselves in order to inform the client quicker about a lost connection
      // the write message task is used to check for the timeout
      lastMessageReceivedTimestamp = Date.now()

      setNetworkStatus(NetworkStatus.CONNECTED) // set status to online
      var transportMessage, header
      try {
        transportMessage = JSON.parse(message.data)
        header = parseHeader(transportMessage.h)
      } catch (e) {
        header = parseHeaderFallback(message.data)
        transportMessage = parseTransportMessageFallback(message.data, header)
        communication.sendCommunicationErrorInfoMessage(
          "Client failed to parse message",
          e,
          header.messageId,
          message.data
        )
      }

      if (serverLastAckedId < header.lastReceivedId) {
        // update the id of the last message the server has ack'ed
        serverLastAckedId = header.lastReceivedId

        // remove acked messages from outBuffer
        outBuffer.removeAckedMessages(serverLastAckedId)

        // and also remove them from the persistent contribution queue
        persistentContributionQueue.removeAckedMessages(serverLastAckedId)
      }

      // if initialAckTimeoutTimer is set, then we wait for the first ack from the server
      // this will also start the writeMessages "thread"
      if (initialAckTimeoutTimer) {
        connecting = false
        // Initial ack from server received
        if (writeTimer) {
          log.error("write timer is running during initial ACK???!")
        }
        handleInitialAckFromServer(header)
      }

      if (transportMessage.payload) {
        if (header.messageId === lastReceivedId + 1) {
          // log.info("Received regular message: " + header.messageId);
          ackRequired = true
          lastReceivedId++
          var realMessage = transportMessage.payload
          if (compressionEnabled) {
            var data, string
            try {
              data = atob(realMessage) // de-base64
              string = pako.inflate(data, { to: "string" }) // inflate
              realMessage = JSON.parse(string)
            } catch (e) {
              communication.sendCommunicationErrorInfoMessage(
                "Client failed to parse compressed payload in message",
                e,
                header.messageId,
                string ? string : message.data
              )
              return // no message to handle
            }
          }
          logping.info(
            "<<< Received message #" + header.messageId + ": ",
            realMessage,
            " timestamp: ",
            lastMessageReceivedTimestamp
          )
          app.manager.safeTrigger("communication:messageReceived", realMessage)
        } else if (header.messageId <= lastReceivedId) {
          log.info("Ignoring incoming duplicate message with ID " + header.messageId)
          // pending #15873 maybe send info message to server about duplicated message to allow monitoring of network quality
        } else {
          log.warn("Message got lost: expected " + (lastReceivedId + 1) + " but got " + header.messageId)
          // pending #15873 maybe send info message to server about lost message to allow monitoring of network quality
        }
      }
    } catch (e) {
      log.error("Error in onMessage: ", e.message, " - ", message.data, e) // JSON.stringify(message) does not encode all of the MessageEvent
    }
  }

  /**
   * Parse the header, the format is: AckIdFromServer/IdOfReceivedMessage
   * The "/IdOfReceivedMessage"-part is optional if just a high-level "ping"
   * is received.
   *
   * @param header
   * @returns {{lastReceivedId: Number, messageId: *}}
   */
  function parseHeader(header) {
    var lastReceivedId, messageId
    if (header) {
      var parts = header.split("/")
      lastReceivedId = parseInt(parts[0])
      if (parts.length > 1) {
        messageId = parseInt(parts[1])
      }
    }
    return { lastReceivedId: lastReceivedId, messageId: messageId }
  }

  /**
   * Fallback version of parseHeader in case the JSON.parse() failed
   * try to use a regexp to get the message IDs.
   *
   * @param string
   * @returns {{lastReceivedId: Number, messageId: *}}
   */
  function parseHeaderFallback(string) {
    var lastReceivedId = null,
      messageId = null
    var match = string.match(/^\s*{\s*"h"\s*:\s*"(\d+)(?:\/(\d+))?"/)
    if (match) {
      lastReceivedId = parseInt(match[1])
      if (match[2]) {
        messageId = parseInt(match[2])
      }
    }
    return { lastReceivedId: lastReceivedId, messageId: messageId }
  }

  /**
   * Fallback version to generate a dummy transport message in case the JSON.parse() failed
   *
   * @param string
   * @param header - the header that has already been parsed
   * @returns object (transportMessage)
   */
  function parseTransportMessageFallback(string, header) {
    if (header.messageId) {
      // there was obviously some message - return a "NoOpMessage" instead
      // todo PET NOW - if compressionEnabled, compress payload .....! ###############################
      return { h: header, payload: { messageType: "NoOpMessage", data: string } }
    }
    // there was no message .... how comes the parse error then .....???
    return { h: header }
  }

  /**
   * handleInitialAckFromServer is called from onMessage() when
   * the first message from the server is received (i.e. the initialAckTimeoutTimer is still running)
   *
   * When connecting to a "new" server (i.e. lastReceivedId === -1)
   * the connection is reset.
   *
   * @param header
   */
  function handleInitialAckFromServer(header) {
    // We got it - cancel the reconnect-timer
    stopInitialAckTimeoutTimer()

    if (header.lastReceivedId === -1) {
      log.info("Connected to new server-side connection - resetting connection and triggering ConnectMessage")
      outBuffer.clear()
      // the persistentContributionQueue is NOT cleared as these messages really should be resent
      lastReceivedId = -1
      serverLastAckedId = -1
      ackRequired = false

      // wait a wee bit before sending the ConnectMessage to (maybe) avoid app crashes on iPod touch with old app ...
      setTimeout(function() {
        app.manager.trigger("communication:connectionEstablished")
      }, 17)
    } else {
      log.info("Connected to same server-side connection - no need to trigger ConnectMessage")
    }

    // Start message writer "thread"
    startWriteTimer()
  }

  // --------------------------------- Sending messages ---------------------------------

  /**
   * Stores the message as json string in the outBuffer waiting to be written by the write timer.
   *
   * @param message The message to be sent to the server.
   */
  function sendMessage(message) {
    var payload = JSON.stringify(message)
    var uncompressedPayload = payload

    try {
      if (compressionEnabled) {
        var data = pako.deflate(payload, { to: "string" }) // deflate
        payload = btoa(data)
      }
    } catch (e) {
      log.error("Failed to deflate payload in send message: ", payload, e)
      return
    }
    var msgId = outBuffer.addMessage(message.messageType, payload)
    logping.info(">>> Outgoing message #" + msgId + ": ", JSON.stringify(message), ")")

    persistentContributionQueue.storeMessage(msgId, message.messageType, uncompressedPayload)
    //startWriteTimer(); // pending ... in future we might want to only schedule the writeMessages timeout if we really have s.th. to write
  }

  // --------------------------------- Writing messages ---------------------------------

  function startWriteTimer() {
    if (!writeTimer) {
      var interval = getConfigParameter("websocketSendInterval")
      // logping.debug("***** start WriteTimer with interval = ", interval);
      writeTimer = setTimeout(writeMessages, interval)
      //logping.info("***** started WriteTimer with interval = ", interval, " / ", writeTimer);
    }
    /*else {
				   logping.debug("***** WriteTimer already running");
			   }*/
  }

  function stopWriteTimer() {
    if (writeTimer) {
      clearTimeout(writeTimer)
      writeTimer = null
      logping.info("CLOSE: WriteTimer stopped")
    }
  }

  // Called repeatedly by writer-"thread"
  function writeMessages() {
    writeTimer = null // the timeout has been triggered

    // first check for server timeout if we must go offline
    var now = Date.now()
    // logping.debug(">>> Writing...", lastMessageReceivedTimestamp, " / ", now, " / ", (now - lastMessageReceivedTimestamp));
    if (now - lastMessageReceivedTimestamp > offlineTimeout) {
      log.warn(
        "Didn't receive a message from server for more than ",
        offlineTimeout,
        "ms - going to reconnect: ",
        now,
        " - ",
        lastMessageReceivedTimestamp,
        " = ",
        now - lastMessageReceivedTimestamp
      )
      // Didn't receive anything from the server in time, so close websocket and reconnect
      close(CloseEventCode.CLOSE_TIMEOUT, "WebsocketConnection timed out")
      delayedConnect()
      return // if the server is offline, don't try to send messages
    }

    //log.trace("check to write messages in out buffer");
    var messagesToWrite = outBuffer.retrieveMessagesToWrite()
    if (messagesToWrite.length === 0) {
      if (ackRequired) {
        // send ack only
        // logping.debug("***** Writing ack: ", lastReceivedId);
        websocket.send('{"h":' + lastReceivedId + "}")
        ackRequired = false
      } // else: no ack, no messages -> nothing to write
      startWriteTimer() // needed for test of lastMessageReceivedTimestamp
      return
    }
    // logping.debug("---- going to write messages: ", messagesToWrite.length);

    try {
      messagesToWrite.forEach(function(message) {
        var msg = '{"h": "' + lastReceivedId + "/" + message.id + '","payload":'
        if (compressionEnabled) {
          msg += '"' + message.payload + '"}'
        } else {
          msg += message.payload + "}"
        }
        logping.debug(">>> Writing message #" + message.id + " (lastIn: " + lastReceivedId + ")")
        websocket.send(msg)
        // logping.trace("--- Messages just written (lastReceivedId ", lastReceivedId, "): ", message.id, ": ", message.payload);
        ackRequired = false
      })
    } catch (e) {
      log.error("Failed to write messages: ", JSON.stringify(messagesToWrite), e)
    }
    startWriteTimer() // needed for test of lastMessageReceivedTimestamp
  }

  // --------------------------------- Close & cleanup ---------------------------------

  /**
   * close the websocket
   *
   * calling this function will NOT try to reconnect / to reconnect connection
   *
   * Public (used by ServerCommunication)
   *
   * @param code - for list of close status codes see https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#Status_codes
   * @param reason
   */
  function close(code, reason) {
    if (closing) {
      log.info("CLOSE: Skipping close - already closing: ", code, " / ", reason)
      return
    }
    try {
      closing = true
      if (!code) {
        code = CloseEventCode.CLOSE_SHUTDOWN
        reason = "stopCommunication called"
      }
      log.debug("CLOSE: Closing connection: ", code, " / reason: ", reason)
      // Stop timers for writing outgoing messages and for waiting for initial ack from server
      stopWriteTimer()
      stopInitialAckTimeoutTimer()
      log.debug("CLOSE: InitialAckTimeoutTimer stopped")

      if (websocket && websocket.readyState !== websocket.CLOSING && websocket.readyState !== websocket.CLOSED) {
        log.debug("CLOSE: Closing websocket: ", code, " / reason ", reason, " / readyState: ", websocket.readyState)
        websocket.close(code, reason) // Will trigger onClose
      }
    } catch (e) {
      log.error("Failed to close websocket: ", websocket ? websocket.readyState : "", e)
    } finally {
      websocket = null
      connecting = false
      closing = false
    }
  }

  function onClose(event) {
    if (closing) {
      // Coming from close - no need to shutdown (again) and trigger reconnect
      log.info("CLOSE: Connection is now closed: ", event.code, " / ", event.reason)
    } else {
      var currentWebsocket = websocket === event.target
      if (currentWebsocket) {
        log.debug(
          "Websocket onClose: ",
          event.code,
          " / ",
          event.reason,
          " was clean: ",
          event.wasClean,
          " type: ",
          event.type
        )
        var code = event.code
        var reason = event.reason

        if (event.code > 1000 && event.code < 3000) {
          // Invalid to call close with this code - we use our own instead
          code = CloseEventCode.CLOSE_RECONNECT
          if (!reason) {
            reason = "websocket closed with code " + event.code + " - going to reconnect"
          }
        }
        close(code, reason)
        delayedConnect()
      }
      // We can't assume 1006 always means unauthenticated access (HTTP 401). It also appears when mobile devices reconnect after an inactive period.
      // ToDo: Refactor websocket security to reject unauthenticated connection attempts according to (see DEV-624):
      // ToDo: https://stackoverflow.com/questions/21762596/how-to-read-status-code-from-rejected-websocket-opening-handshake-with-javascrip
      // else if(event.code == 1006) {
      // 	log.info("Received websocket close event with code ", event.code," without existing websocket instance - Assuming an HTTP 401 for an unauthenticated access attempt - Triggering authentication")
      // 	triggerAuthentication();
      //}
      else {
        log.debug(
          "Ignoring: Websocket onClose of old instance: ",
          event.code,
          " / ",
          event.reason,
          " was clean: ",
          event.wasClean,
          " type: ",
          event.type
        )
      }
    }
  }

  function onError(event) {
    if (closing) {
      // Coming from close - no need to shutdown (again) and trigger reconnect
      log.info("CLOSE: websocket error: ", event.code, " / ", event.reason)
    } else {
      if (websocket === event.target) {
        if (websocket.readyState === websocket.CLOSING || websocket.readyState === websocket.CLOSED) {
          log.debug(
            "Websocket onError (server may be still starting up): ",
            event.code,
            " reason: ",
            event.reason,
            " was clean: ",
            event.wasClean,
            " type: ",
            event.type,
            " websocket readyState: ",
            websocket ? websocket.readyState : ""
          )
        } else {
          // Log error only if websocket is not closing (2) or closed (3)
          log.error(
            "Websocket onError: ",
            event.code,
            " reason: ",
            event.reason,
            " was clean: ",
            event.wasClean,
            " type: ",
            event.type,
            " websocket readyState: ",
            websocket ? websocket.readyState : ""
          )
        }

        close(event.code, event.reason)
        delayedConnect()
      } else {
        log.debug(
          "Ignoring: Websocket onError of old instance: ",
          event.code,
          " reason: ",
          event.reason,
          " was clean: ",
          event.wasClean,
          " type: ",
          event.type
        )
      }
    }

    if (typeof forceRelogin === "function" && apiMeCounter < 1) {
      apiMeCounter++
      // if auth token has been invalidated, trigger re-authentication
      fetch(baseURL + "/api/me", {
        headers: {
          Authorization: 'Bearer ' + accessToken
        }
      })
        .then(response => {
          if (response.status === 401) {
            // function forceRelogin is defined in auth.js (participants only)
            forceRelogin()
          }
        })
    }
  }

  /**
   * Sets the network status to disconnected and connects again after a randomized wait time.
   */
  function delayedConnect() {
    if (connecting) {
      log.info("OPEN: Skipping delayedConnect - already connecting")
    }
    if (closing) {
      log.info("CLOSE: Skipping delayedConnect - still stopping")
    }
    setNetworkStatus(NetworkStatus.DISCONNECTED)
    var min = getConfigParameter("websocketReconnectWaitTimeMin")
    var max = getConfigParameter("websocketReconnectWaitTimeMax")
    var connectDelay = Math.randomInt(min, max)
    log.info("Wait a while before trying to reconnect: ", connectDelay)
    setTimeout(connect, connectDelay)
  }

  /**
   * Sets the status of the server connection
   * @param status 0 - OK, 1 - Warning, 2 - Error
   */
  function setNetworkStatus(/* NetworkStatus */ status) {
    if (networkStatus !== status) {
      log.info("Setting network status from ", networkStatus.name, " to ", status.name)
      networkStatus = status
      setTimeout(function() {
        app.manager.safeTrigger("network:connectionStatus", status.code)
      }, 3)
    }
  }

  function getStatus() {
    return {
      websocket: websocket,
      url: url,
      networkStatus: networkStatus,
      outBuffer: outBuffer.getStatus(),
      lastReceivedId: lastReceivedId,
      ackRequired: ackRequired,
      writeTimer: writeTimer,
      initialAckTimeoutTimer: initialAckTimeoutTimer,
      self: self,
    }
  }

  // Public API
  return {
    connect: connect,
    sendMessage: sendMessage,
    close: close,
    CloseEventCode: CloseEventCode,
    getStatus: getStatus, // for debugging
  }
}

if (typeof module === "object") {
  module.exports = WebsocketConnection
}
